/**
 * Statistics Service for TheAdsFather Homepage
 * Aggregates data from multiple API endpoints to provide comprehensive statistics
 */

import { apiService } from './api';

export interface StatisticsData {
  // Overall counts
  totalAds: number;
  totalCreatives: number;  // Count of unique creative numbers
  totalProductions: number;
  totalJobs: number;
  totalLicensedSongs: number;
  totalUploaded: number;  // Total ads with at least one platform upload

  // Latest ad
  latestAd?: {
    ad_id: string;
    video_url: string;
    created_by: string;
    created_at: string;
    production_id: number;
    creative_number: number;
    ratio: string;
    duration: number;
    tags?: {
      cast?: string;
      heroAge?: string;
      heroGender?: string;
      pianoType?: string;
      licensedSong?: string[] | string;
      meantTo?: string[];
    };
  };

  // Metadata breakdowns
  metadataBreakdown: {
    cast: Record<string, number>;
    heroAge: Record<string, number>;
    heroGender: Record<string, number>;
    pianoType: Record<string, number>;  // Piano type -> count
    licensedSongs: Record<string, number>;  // Song name -> count
    meantTo: Record<string, number>;  // USP -> count
  };

  // Platform distribution (by markers, not jobs)
  platformMarkers: {
    youtube: number;      // Ads with youtube_job_id
    meta: number;         // Ads with meta_job_id
    both: number;         // Ads with both markers
    none: number;         // Ads with no platform markers
  };

  // Job statistics (kept for compatibility)
  jobsByStatus: Record<string, number>;
  jobsByPlatform: Record<string, number>;
  jobsBySource: Record<string, number>;

  // Licensed songs status
  licensedSongsByStatus: {
    active: number;
    expired: number;
    expiringSoon: number;
    noExpiry: number;
  };

  // Recent activity (last 30 days)
  recentJobs: number;
  recentAds: number;

  // Loading states
  loading: boolean;
  error?: string;
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
  }[];
}

class StatisticsService {
  private cache: StatisticsData | null = null;
  private cacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get comprehensive statistics data
   */
  async getStatistics(): Promise<StatisticsData> {
    // Check cache
    if (this.cache && Date.now() - this.cacheTime < this.CACHE_DURATION) {
      return this.cache;
    }

    const stats: StatisticsData = {
      totalAds: 0,
      totalCreatives: 0,
      totalProductions: 0,
      totalJobs: 0,
      totalLicensedSongs: 0,
      totalUploaded: 0,
      metadataBreakdown: {
        cast: {},
        heroAge: {},
        heroGender: {},
        pianoType: {},
        licensedSongs: {},
        meantTo: {},
      },
      platformMarkers: {
        youtube: 0,
        meta: 0,
        both: 0,
        none: 0,
      },
      jobsByStatus: {},
      jobsByPlatform: {},
      jobsBySource: {},
      licensedSongsByStatus: {
        active: 0,
        expired: 0,
        expiringSoon: 0,
        noExpiry: 0,
      },
      recentJobs: 0,
      recentAds: 0,
      loading: true,
    };

    try {
      // Fetch data from multiple endpoints in parallel
      const [jobsResponse, productions, ads, licensedSongs] = await Promise.allSettled([
        apiService.getAllJobs({ limit: 1000 }),
        apiService.getProductions(),
        apiService.getAllAds(10000),
        apiService.getLicensedSongs(),
      ]);

      // Process jobs data
      if (jobsResponse.status === 'fulfilled') {
        const jobsData = jobsResponse.value;
        stats.totalJobs = jobsData.total_count;
        stats.jobsByStatus = jobsData.summary.by_status;
        stats.jobsByPlatform = jobsData.summary.by_platform;
        stats.jobsBySource = jobsData.summary.by_source;

        // Count recent jobs (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        stats.recentJobs = jobsData.jobs.filter(job => {
          const jobDate = new Date(job.created_at);
          return jobDate >= thirtyDaysAgo;
        }).length;
      }

      // Process productions data
      if (productions.status === 'fulfilled') {
        stats.totalProductions = productions.value.length;
      }

      // Process ads data
      if (ads.status === 'fulfilled') {
        const adsData = ads.value;
        stats.totalAds = adsData.length;

        // Count unique creative numbers
        const uniqueCreatives = new Set(adsData.map(ad => ad.creative_number));
        stats.totalCreatives = uniqueCreatives.size;

        // Count ads with platform uploads
        stats.totalUploaded = adsData.filter(ad => 
          ad.youtube_job_id || ad.meta_job_id
        ).length;

        // Find latest ad
        if (adsData.length > 0) {
          const sortedAds = [...adsData].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          const latest = sortedAds[0];
          stats.latestAd = {
            ad_id: latest.ad_id,
            video_url: latest.video_url,
            created_by: latest.created_by,
            created_at: latest.created_at,
            production_id: latest.production_id,
            creative_number: latest.creative_number,
            ratio: latest.ratio,
            duration: latest.duration,
            tags: latest.tags,
          };
        }

        // Compute metadata breakdowns
        adsData.forEach(ad => {
          // Platform markers
          const hasYoutube = !!ad.youtube_job_id;
          const hasMeta = !!ad.meta_job_id;
          
          if (hasYoutube && hasMeta) {
            stats.platformMarkers.both++;
          } else if (hasYoutube) {
            stats.platformMarkers.youtube++;
          } else if (hasMeta) {
            stats.platformMarkers.meta++;
          } else {
            stats.platformMarkers.none++;
          }

          // Metadata tags
          if (ad.tags) {
            // Cast
            const cast = ad.tags.cast || 'unknown';
            stats.metadataBreakdown.cast[cast] = (stats.metadataBreakdown.cast[cast] || 0) + 1;

            // Hero Age
            const heroAge = ad.tags.heroAge || 'unknown';
            stats.metadataBreakdown.heroAge[heroAge] = (stats.metadataBreakdown.heroAge[heroAge] || 0) + 1;

            // Hero Gender
            const heroGender = ad.tags.heroGender || 'unknown';
            stats.metadataBreakdown.heroGender[heroGender] = (stats.metadataBreakdown.heroGender[heroGender] || 0) + 1;

            // Piano Type
            const pianoType = ad.tags.pianoType || 'unknown';
            stats.metadataBreakdown.pianoType[pianoType] = (stats.metadataBreakdown.pianoType[pianoType] || 0) + 1;

            // Licensed Songs
            const licensedSong = ad.tags.licensedSong;
            if (licensedSong) {
              const songs = Array.isArray(licensedSong) ? licensedSong : [licensedSong];
              songs.forEach(song => {
                if (song && song.trim()) {
                  stats.metadataBreakdown.licensedSongs[song] = 
                    (stats.metadataBreakdown.licensedSongs[song] || 0) + 1;
                }
              });
            }

            // USPs (meantTo)
            const meantTo = ad.tags.meantTo;
            if (meantTo && Array.isArray(meantTo)) {
              meantTo.forEach(usp => {
                if (usp && usp.trim()) {
                  stats.metadataBreakdown.meantTo[usp] = 
                    (stats.metadataBreakdown.meantTo[usp] || 0) + 1;
                }
              });
            }
          }
        });

        // Count recent ads (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        stats.recentAds = adsData.filter(ad => {
          const adDate = new Date(ad.created_at);
          return adDate >= thirtyDaysAgo;
        }).length;
      }

      // Process licensed songs data
      if (licensedSongs.status === 'fulfilled') {
        const songsData = licensedSongs.value;
        stats.totalLicensedSongs = songsData.summary.total_songs;
        stats.licensedSongsByStatus = {
          active: songsData.summary.active,
          expired: songsData.summary.expired,
          expiringSoon: songsData.summary.expiring_soon,
          noExpiry: songsData.summary.no_expiry_date,
        };
      }

      stats.loading = false;

      // Cache the results
      this.cache = stats;
      this.cacheTime = Date.now();

      return stats;
    } catch (error) {
      stats.loading = false;
      stats.error = error instanceof Error ? error.message : 'Failed to load statistics';
      return stats;
    }
  }

  /**
   * Get chart data for jobs by status
   */
  getJobsByStatusChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.jobsByStatus);
    const data = Object.values(stats.jobsByStatus);

    return {
      labels,
      datasets: [{
        label: 'Jobs by Status',
        data,
        backgroundColor: [
          '#4caf50', // success
          '#ff9800', // pending
          '#f44336', // failed
          '#2196f3', // processing
          '#9c27b0', // other
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for jobs by platform
   */
  getJobsByPlatformChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.jobsByPlatform);
    const data = Object.values(stats.jobsByPlatform);

    return {
      labels,
      datasets: [{
        label: 'Jobs by Platform',
        data,
        backgroundColor: [
          '#ff0000', // YouTube
          '#1877f2', // Meta
          '#4285f4', // Google
          '#1da1f2', // Twitter
          '#e91e63', // Other
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for licensed songs by status
   */
  getLicensedSongsChartData(stats: StatisticsData): ChartData {
    const statusData = stats.licensedSongsByStatus;
    const labels = ['Active', 'Expired', 'Expiring Soon', 'No Expiry'];
    const data = [
      statusData.active,
      statusData.expired,
      statusData.expiringSoon,
      statusData.noExpiry,
    ];

    return {
      labels,
      datasets: [{
        label: 'Licensed Songs',
        data,
        backgroundColor: [
          '#4caf50', // active
          '#f44336', // expired
          '#ff9800', // expiring soon
          '#9e9e9e', // no expiry
        ],
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for platform markers distribution
   */
  getPlatformMarkersChartData(stats: StatisticsData): ChartData {
    const { youtube, meta, both, none } = stats.platformMarkers;
    
    return {
      labels: ['YouTube Only', 'Meta Only', 'Both Platforms', 'Not Uploaded'],
      datasets: [{
        label: 'Platform Distribution',
        data: [youtube, meta, both, none],
        backgroundColor: [
          '#ff0000', // YouTube red
          '#1877f2', // Meta blue
          '#9c27b0', // Purple for both
          '#9e9e9e', // Gray for none
        ],
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for cast distribution
   */
  getCastChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.metadataBreakdown.cast);
    const data = Object.values(stats.metadataBreakdown.cast);

    return {
      labels,
      datasets: [{
        label: 'Cast Type',
        data,
        backgroundColor: [
          '#2196f3', // Kids
          '#e91e63', // Couple
          '#4caf50', // Solo actor
          '#ff9800', // No Human actor
          '#9c27b0', // Human with Prompt
          '#795548', // hand only
          '#00bcd4', // Family
          '#9e9e9e', // unknown
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for hero age distribution
   */
  getHeroAgeChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.metadataBreakdown.heroAge);
    const data = Object.values(stats.metadataBreakdown.heroAge);

    return {
      labels,
      datasets: [{
        label: 'Hero Age',
        data,
        backgroundColor: [
          '#673ab7', // Senior
          '#2196f3', // Adult
          '#4caf50', // Teenage
          '#ff9800', // Kid
          '#9e9e9e', // unknown
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for hero gender distribution
   */
  getHeroGenderChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.metadataBreakdown.heroGender);
    const data = Object.values(stats.metadataBreakdown.heroGender);

    return {
      labels,
      datasets: [{
        label: 'Hero Gender',
        data,
        backgroundColor: [
          '#e91e63', // Female
          '#2196f3', // Male
          '#9c27b0', // other
          '#9e9e9e', // unknown
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for piano type distribution
   */
  getPianoTypeChartData(stats: StatisticsData): ChartData {
    const labels = Object.keys(stats.metadataBreakdown.pianoType);
    const data = Object.values(stats.metadataBreakdown.pianoType);

    return {
      labels,
      datasets: [{
        label: 'Piano Type',
        data,
        backgroundColor: [
          '#8b4513', // Piano (brown)
          '#4169e1', // Keyboard (blue)
          '#9c27b0', // Mixed (purple)
          '#9e9e9e', // No instrument (gray)
          '#757575', // unknown (dark gray)
        ].slice(0, labels.length),
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for top licensed songs
   */
  getTopLicensedSongsChartData(stats: StatisticsData, topN: number = 10): ChartData {
    const entries = Object.entries(stats.metadataBreakdown.licensedSongs);
    const sorted = entries.sort((a, b) => b[1] - a[1]).slice(0, topN);
    
    return {
      labels: sorted.map(([song]) => song),
      datasets: [{
        label: 'Licensed Songs Usage',
        data: sorted.map(([, count]) => count),
        backgroundColor: '#4caf50',
        borderWidth: 1,
      }],
    };
  }

  /**
   * Get chart data for top meantTo tags
   */
  getTopUSPsChartData(stats: StatisticsData, topN: number = 10): ChartData {
    const entries = Object.entries(stats.metadataBreakdown.meantTo);
    const sorted = entries.sort((a, b) => b[1] - a[1]).slice(0, topN);
    
    return {
      labels: sorted.map(([tag]) => tag),
      datasets: [{
        label: 'Meant To Tags',
        data: sorted.map(([, count]) => count),
        backgroundColor: '#2196f3',
        borderWidth: 1,
      }],
    };
  }

  /**
   * Clear cache (useful for manual refresh)
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTime = 0;
  }
}

// Export singleton instance
export const statisticsService = new StatisticsService();

